import {useCallback, useMemo, useState} from 'react';

import type {UserFacingEntity} from '../../../../../../ui/core-entity/rows/peep-rows/types';

import type {SelectMenuOption} from '../../select-menu/select-menu';

import {BaseRow} from '../base-row/base-row';
import {getBusinessCardRowToolbarActions} from '../base-row/helpers';
import {RenameableRowTitle} from '../base-row/renameable-row-title';
import {RowValue} from '../base-row/row-value';
import {useDeleteConfirmation} from '../base-row/use-delete-confirmation';
import {useValueSaveToastTracker} from '../base-row/use-value-save-toast-tracker';

import {AddressRowEditState} from './address-row-edit-state';
import type {AddressValue} from './types';

interface Props {
    title?: string;
    isPrimary?: boolean;
    showIsPrimaryIcon?: boolean;
    isFormer?: boolean;
    canMarkAsPrimary?: boolean;
    fullAddress: string;
    addressValue: AddressValue;
    parentEntity: UserFacingEntity;
    onUpdateAddress: (addressValue: AddressValue) => Promise<any>;
    onDelete: () => Promise<any>;
    onUpdateTitle: (title: string) => Promise<any>;
    onMarkAsPrimary?: () => Promise<any>;
    onChangeFormerStatus?: (isFormer: boolean) => Promise<any>;
    dropdownOptions?: SelectMenuOption[];
    successFlashKey?: string;
}

export function AddressRow(props: Props) {
    const {onUpdateAddress, isFormer, showIsPrimaryIcon = true} = props;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isMutatingToolbar, setIsMutatingToolbar] = useState<boolean>(false);
    const [isTitleDropdownOpen, setIsTitleDropdownOpen] = useState<boolean>(false);
    const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
    const [isCopied, setIsCopied] = useState<boolean>(false);

    const {requestDelete, isDeleteConfirmOpen, deleteConfirmationDialog, deleteAnchorRef} =
        useDeleteConfirmation(props.onDelete, setIsMutatingToolbar, 'address', props.parentEntity);

    const {valueSaveToastState, setValueSaveToastState, trackValueSaveToastForPromise} =
        useValueSaveToastTracker();

    const handleRowClick = useCallback(() => {
        if (isEditing || isFormer) {
            return;
        }

        setIsEditing(true);
    }, [isEditing, isFormer]);

    const handleAddressUpdate = useCallback(
        (addressValue: AddressValue) => {
            return trackValueSaveToastForPromise(
                onUpdateAddress(addressValue).then((result) => {
                    setIsEditing(false);
                    return result;
                })
            );
        },
        [onUpdateAddress, trackValueSaveToastForPromise]
    );

    const toolbarActions = useMemo(() => {
        return getBusinessCardRowToolbarActions(
            {
                isFormer: props.isFormer,
                canMarkAsPrimary: props.canMarkAsPrimary && !props.isPrimary,
                canChangeFormerStatus: props.onChangeFormerStatus !== undefined,
            },
            {
                onMarkAsPrimary: props.onMarkAsPrimary,
                onChangeFormerStatus: props.onChangeFormerStatus,
                onEdit: () => setIsEditing(true),
                onDelete: requestDelete,
                onDeleteRef: deleteAnchorRef,
                onCopy: (isCopied: boolean) => setIsCopied(isCopied),
            },
            props.fullAddress,
            isCopied,
            setIsMutatingToolbar
        );
    }, [
        props.isPrimary,
        props.isFormer,
        props.canMarkAsPrimary,
        props.onMarkAsPrimary,
        props.onChangeFormerStatus,
        requestDelete,
        deleteAnchorRef,
        props.fullAddress,
        isCopied,
    ]);

    return (
        <>
            <BaseRow
                type='address'
                onClick={handleRowClick}
                successFlashKey={props.successFlashKey}
                isPrimary={showIsPrimaryIcon ? props.isPrimary : undefined}
                toolbarProps={{
                    ...toolbarActions,
                    forceShowToolbar: isDeleteConfirmOpen,
                    forceHideToolbar:
                        isEditing || isMutatingToolbar || isTitleDropdownOpen || isEditingTitle,
                }}
            >
                <RenameableRowTitle
                    title={props.title ?? 'Address'}
                    options={props.dropdownOptions ?? []}
                    onUpdateTitle={props.onUpdateTitle}
                    saveToastState={valueSaveToastState}
                    setSaveToastState={setValueSaveToastState}
                    onDropdownOpenChange={setIsTitleDropdownOpen}
                    setIsEditingTitle={setIsEditingTitle}
                    isCopied={isCopied}
                />
                {isEditing ? (
                    <AddressRowEditState
                        addressValue={props.addressValue}
                        isEditing={isEditing}
                        setIsEditing={setIsEditing}
                        updateAddress={handleAddressUpdate}
                        preventSaveOnBlur={true}
                        ignorePasswordAutoFill={true}
                    />
                ) : (
                    <RowValue value={props.fullAddress} />
                )}
            </BaseRow>
            {deleteConfirmationDialog}
        </>
    );
}
